import React, { useMemo, useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { AlertCircle, ArrowLeft } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import type { AuthError } from '@/contexts/AuthContext'
import { DiamondIcon, GoldText, LuxuryButton } from '@/components/UI'
import {
  clearPostLoginRedirect,
  readPostLoginRedirect,
  safePostLoginRedirect,
} from '@/services/postLoginRedirect'

type VerifyEmailLocationState = {
  email?: string
  message?: string
  returnTo?: string
}

const getStoredPendingEmail = () => {
  try {
    return localStorage.getItem('pending_verification_email') || ''
  } catch {
    return ''
  }
}

export const VerifyEmailPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { verifyEmailCode, isLoading, error: authError } = useAuth()

  const locationState = (location.state as VerifyEmailLocationState) || {}
  const queryEmail = useMemo(() => new URLSearchParams(location.search).get('email') || '', [location.search])
  const postVerificationRedirect = useMemo(
    () => safePostLoginRedirect(locationState.returnTo) || readPostLoginRedirect() || '/app',
    [locationState.returnTo],
  )

  const [email] = useState(() => locationState.email || queryEmail || getStoredPendingEmail())
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (email) {
      try {
        localStorage.setItem('pending_verification_email', email)
      } catch {
        // ignore storage errors (private mode, etc.)
      }
    }
  }, [email])

  useEffect(() => {
    if (!email) {
      setError('Session expired. Please log in again to receive a new verification code.')
    }
  }, [email])

  const combinedError = error || authError
  const canSubmit = useMemo(() => !!email && /^\d{6}$/.test(code.trim()), [email, code])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    try {
      await verifyEmailCode(email.trim().toLowerCase(), code.trim())
      try {
        localStorage.removeItem('pending_verification_email')
      } catch {
        /* ignore */
      }
      clearPostLoginRedirect()
      navigate(postVerificationRedirect, { replace: true })
    } catch (err) {
      const authErr = err as AuthError
      if (authErr?.code === 'CODE_EXPIRED') {
        setError('Code expired. Please request a new one by re-running signup or login.')
      } else {
        setError('Invalid code. Please check the 6 digits and try again.')
      }
    }
  }

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--bg-canvas)' }}>
      <motion.div
        initial={{ opacity: 0, x: -40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
        className="hidden lg:flex lg:w-2/5 relative overflow-hidden"
        style={{ background: 'linear-gradient(145deg, rgba(var(--accent-glow-rgba),0.1) 0%, rgba(var(--accent-glow-rgba),0.05) 100%)' }}
      >
        <div className="absolute inset-0 opacity-30" style={{ background: 'radial-gradient(circle at 25% 30%, rgba(var(--accent-glow-rgba),0.2), transparent 55%)' }} />
        <div className="absolute inset-0 opacity-30" style={{ background: 'radial-gradient(circle at 70% 70%, rgba(var(--accent-glow-rgba),0.15), transparent 55%)' }} />

        <div className="relative z-10 w-full px-12 py-16 flex flex-col justify-between">
          <div>
            {/* Diamond Badge */}
            <div
              className="flex items-center gap-4 mb-8"
              style={{
                color: 'var(--accent)',
                textTransform: 'uppercase',
                letterSpacing: 'var(--tracking-label)',
                fontSize: '0.84rem',
                fontWeight: 600,
              }}
            >
              <DiamondIcon size="md" glow />
              <span>Almost There</span>
              <DiamondIcon size="md" glow />
            </div>

            <h1
              style={{
                fontFamily: 'var(--font-family-display)',
                fontSize: 'var(--font-size-display-md)',
                lineHeight: 'var(--line-height-display)',
                letterSpacing: 'var(--tracking-display)',
                fontWeight: 700,
                marginBottom: '1rem',
                color: 'var(--text-primary)',
              }}
            >
              Verify Your
              <br />
              <GoldText as="span" glow>
                Identity
              </GoldText>
            </h1>
            <p className="text-[var(--text-muted)] text-lg mb-8" style={{ lineHeight: 1.6, fontWeight: 400 }}>
              Enter the 6-digit verification code to activate your account. Codes expire in 10 minutes.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <DiamondIcon size="sm" glow />
                <div>
                  <h3
                    className="mb-1"
                    style={{
                      fontFamily: 'var(--font-family-display)',
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                    }}
                  >
                    Check inbox & spam
                  </h3>
                  <p className="text-sm text-[var(--text-muted)]" style={{ lineHeight: 1.5, fontWeight: 400 }}>
                    Search for the code email if you don&apos;t see it right away.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <DiamondIcon size="sm" glow />
                <div>
                  <h3
                    className="mb-1"
                    style={{
                      fontFamily: 'var(--font-family-display)',
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                    }}
                  >
                    Code expires in 10 minutes
                  </h3>
                  <p className="text-sm text-[var(--text-muted)]" style={{ fontWeight: 300 }}>
                    Run a new signup or login if you need another code.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 text-[var(--text-muted)] text-sm" style={{ fontWeight: 300 }}>
            <div className="w-2 h-2 rounded-full bg-[var(--accent)] animate-pulse" />
            Secure by design. We only issue tokens after successful verification.
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full lg:w-3/5 flex items-center justify-center px-6 py-12"
      >
        <div className="w-full max-w-xl">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="rounded-2xl p-8"
            style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', boxShadow: '0 18px 45px -12px rgba(0,0,0,0.35)' }}
          >
            <div className="mb-8">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-sm text-[var(--accent)] hover:underline flex items-center gap-2 mb-6"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to login
              </button>

              <div className="text-center">
                {/* Diamond Badge */}
                <div
                  className="flex items-center justify-center gap-4 mb-6"
                  style={{
                    color: 'var(--accent)',
                    textTransform: 'uppercase',
                    letterSpacing: 'var(--tracking-label)',
                    fontSize: '0.84rem',
                    fontWeight: 600,
                  }}
                >
                  <DiamondIcon size="md" glow />
                  <span>Step 2</span>
                  <DiamondIcon size="md" glow />
                </div>
                <h2
                  style={{
                    fontFamily: 'var(--font-family-display)',
                    fontSize: 'var(--font-size-display-sm)',
                    fontWeight: 700,
                    lineHeight: 'var(--line-height-display)',
                    letterSpacing: 'var(--tracking-tight)',
                    marginBottom: '0.5rem',
                    color: 'var(--text-primary)',
                  }}
                >
                  Verify your{' '}
                  <GoldText as="span" glow>
                    Email
                  </GoldText>
                </h2>
                <p className="text-[var(--text-muted)]" style={{ fontWeight: 400, lineHeight: 1.55 }}>
                  Enter the code to continue securely to Ricald.
                </p>
              </div>
            </div>

            {combinedError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-4 rounded-lg flex items-center gap-3"
                style={{
                  background: 'color-mix(in srgb, var(--state-danger) 12%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--state-danger) 35%, transparent)',
                }}
              >
                <AlertCircle className="w-5 h-5 text-[var(--state-danger)] flex-shrink-0" />
                <p className="text-sm text-[var(--state-danger)]">{combinedError}</p>
              </motion.div>
            )}

            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Verification code</label>
                <input
                  ref={(el) => {
                    if (el) {
                      el.setCustomValidity('')
                      el.addEventListener('invalid', () => {
                        if (el.validity.patternMismatch) {
                          el.setCustomValidity('Please enter exactly 6 digits')
                        } else if (el.validity.valueMissing) {
                          el.setCustomValidity('Verification code is required')
                        }
                      })
                      el.addEventListener('input', () => {
                        el.setCustomValidity('')
                      })
                    }
                  }}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  required
                  className="w-full tracking-[0.4em] text-center px-4 py-3 rounded-lg bg-[var(--bg-input,#111827)] border border-[var(--border)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                  placeholder="123456"
                  aria-label="Six-digit verification code"
                  aria-describedby="code-hint"
                />
                <p id="code-hint" className="text-xs text-[var(--text-muted)] mt-2">Use the 6-digit code from your welcome email.</p>
              </div>

              <LuxuryButton
                type="submit"
                disabled={!canSubmit || isLoading}
                fullWidth
                size="md"
              >
                {isLoading ? 'Verifying...' : 'Verify and Continue'}
              </LuxuryButton>
            </form>

            <div className="mt-6 text-sm text-[var(--text-secondary)] space-y-2">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[var(--accent)] inline-block" />
                If you requested multiple codes, only the newest one works.
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[var(--accent)] inline-block" />
                Need a new code? Re-run signup with the same email to receive another.
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  )
}

export default VerifyEmailPage
